/**
 * Diagnostic Logs page — accessible without authentication.
 * Fetches logs directly from the backend URL, bypassing the SPA proxy.
 */
import React, { useState, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DiagnosticLogs() {
  const [lines, setLines] = useState(200);
  const [tailContent, setTailContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleDownload = useCallback(async () => {
    setError('');
    try {
      const resp = await fetch(`${API_URL}/api/logs/download`);
      if (!resp.ok) throw new Error(`${resp.status} ${resp.statusText}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'app.log';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 150);
    } catch (e) {
      setError(e.message || 'Failed to download logs');
    }
  }, []);

  const handleTail = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const resp = await fetch(`${API_URL}/api/logs/tail?lines=${lines}`);
      if (!resp.ok) throw new Error(`${resp.status} ${resp.statusText}`);
      setTailContent(await resp.text());
    } catch (e) {
      setError(e.message || 'Failed to fetch logs');
    } finally {
      setLoading(false);
    }
  }, [lines]);

  return (
    <div style={{ maxWidth: 960, margin: '2rem auto', padding: '0 1rem', fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '1rem' }}>Diagnostic Logs</h1>

      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <button onClick={handleDownload} style={btnStyle}>Download Full Log</button>

        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          Lines:{' '}
          <input
            type="number"
            min={1}
            max={5000}
            value={lines}
            onChange={e => setLines(Number(e.target.value) || 200)}
            style={{ width: 80, padding: '0.35rem 0.5rem', border: '1px solid #ccc', borderRadius: 4 }}
          />
        </label>
        <button onClick={handleTail} disabled={loading} style={btnStyle}>
          {loading ? 'Loading…' : 'View Tail'}
        </button>
      </div>

      {error && <div style={{ color: '#dc2626', marginBottom: '0.75rem' }}>{error}</div>}

      {tailContent && (
        <pre style={{
          background: '#111', color: '#e5e5e5', padding: '1rem', borderRadius: 6,
          overflow: 'auto', maxHeight: '70vh', fontSize: '0.8rem', lineHeight: 1.5,
          whiteSpace: 'pre-wrap', wordBreak: 'break-all',
        }}>
          {tailContent}
        </pre>
      )}
    </div>
  );
}

const btnStyle = {
  padding: '0.45rem 1rem', background: '#2563eb', color: '#fff',
  border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.875rem',
};
